// One-time hints the first time you get on a ride (#150): the BMX's body
// flips are two stick taps then □, which nobody finds by mashing. Shown once,
// for `HINT_TIME`, and remembered in settings (`Settings.hintsSeen`) so it
// never comes back. Sim-free: Game asks `dueHint` after each step and the
// HUD draws `hintText` in whatever input you're using.
// The pack strip's line on foot (`packKeysText`, #145) lives here too: it
// names the ride's buttons the same way.

import type { ItemId } from "./sim/backpack";
import { isRide, type RideId } from "./sim/rides";

export const HINT_IDS = ["bmxFlips"] as const;
export type HintId = (typeof HINT_IDS)[number];

/** How long a hint stays up (s of sim time). */
export const HINT_TIME = 6;

/** The ride each hint is for. */
const HINT_RIDE: Record<HintId, RideId> = { bmxFlips: "bmx" };

/**
 * The hint to show now: you've just got on (on a ride this step, on foot or
 * on something else the last), it's one with a hint and you haven't seen it.
 */
export function dueHint(was: RideId | null, now: RideId | null, seen: readonly HintId[]): HintId | null {
  if (now === null || now === was) return null;
  return HINT_IDS.find((id) => HINT_RIDE[id] === now && !seen.includes(id)) ?? null;
}

/** Keeps the known ids from saved settings (untrusted), once each. */
export function parseHintsSeen(v: unknown): HintId[] {
  if (!Array.isArray(v)) return [];
  return HINT_IDS.filter((id) => v.includes(id));
}

/** The hint's words for the input you're on; `flip` is that input's label for the flip button. */
export function hintText(id: HintId, source: "gamepad" | "keyboard" | "touch", flip: string): string {
  switch (id) {
    case "bmxFlips":
      if (source === "keyboard") return `In the air: W S then ${flip} backflip · S W then ${flip} frontflip`;
      if (source === "touch") return `In the air: stick ↑ ↓ then ${flip} backflip · ↓ ↑ then ${flip} frontflip`;
      return `In the air: ↑ ↓ then ${flip} backflip · ↓ ↑ then ${flip} frontflip`;
  }
}

/** The pack buttons the strip names, by action. */
export type PackAction = "spinL" | "spinR" | "flip" | "nollie" | "revert";

/**
 * The pack strip's line on foot (#136) with `item` out, `label` giving each
 * action's button in the input you're using: switching items, the gun's
 * fire and the washer's spray (#151); with a ride out, the chord that gets on it first (#145).
 */
export function packKeysText(item: ItemId, label: (a: PackAction) => string): string {
  const swap = `${label("spinL")} / ${label("spinR")} switch`;
  if (isRide(item)) return `${label("nollie")} + ${label("revert")} to ride · ${swap}`;
  if (item === "paintball") return `${swap} · ${label("flip")} fire`;
  return item === "washer" ? `${swap} · hold ${label("flip")} spray` : swap;
}
